import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { isCurrentDatePastExpDate } from '../../common/utils';
import { accountApi } from '../../lib/api/account';
import { PermissionType, type Trial } from '../../store/pageFrame/types';
import { useGetStripeSubscription } from '../accounts-api';

type UseAccountsApiDs3AccessQueryResponse = {
  hasDs3Access: boolean;
  trial: Trial;
  isTrialExpired: boolean;
  isValidStripeSubscriptionExpired: boolean;
};

/**
 * A hook to determine if the user has ds3.* in their permissions array
 */
export function useAccountsApiDs3AccessQuery(options?: UseQueryOptions<UseAccountsApiDs3AccessQueryResponse>) {
  const { data: stripeSubcription } = useGetStripeSubscription();
  return useQuery(
    ['has-ds3-access'],
    async () => {
      let trial = await accountApi.getTrial();
      const memberships = await accountApi.getUsersMemberships();

      const hasDs3Permission = memberships?.subscriptions
        ?.filter((s) => s.source !== 'Forms/DS3 Trial')
        .some((s) => s.permissions.includes(PermissionType.DS3));

      const hasValidStripeSubscription =
        !!stripeSubcription &&
        new Date(stripeSubcription.validUntil) > new Date('1970-01-01') &&
        !isCurrentDatePastExpDate(stripeSubcription.validUntil);

      const hasDs3Access = hasDs3Permission || hasValidStripeSubscription;
      if (!hasDs3Access && !trial) {
        trial = await accountApi.createTrial();
      }

      const trialExtensions = trial?.extensions || [];
      const latestTrialExtension = trialExtensions[0];
      const isTrialExpired =
        (isCurrentDatePastExpDate(trial?.endDate?.slice(0, 10) || '') && !latestTrialExtension) ||
        latestTrialExtension?.envelopesLeft === 0;

      return {
        hasDs3Access,
        trial,
        isTrialExpired,
      };
    },
    options
  );
}
